/**
 * Supermicro SYS-821GE-TNHR Server Profile
 *
 * Source: research/server-specs.md
 * - 8U form factor
 * - 8-GPU HGX H100/H200 baseboard
 * - Dual Intel Xeon Scalable (up to 64 cores each)
 * - Up to 8TB DDR5
 * - 8x 3000W PSU (4+4 redundancy)
 * - Air-cooled or liquid-cooling ready
 */

import type { ServerProfile } from '../../types/servers';

/**
 * Supermicro SYS-821GE-TNHR with HGX H100 80GB
 */
export const SUPERMICRO_SYS821GE_H100: ServerProfile = {
  id: 'supermicro-sys821ge-h100',
  name: 'Supermicro SYS-821GE-TNHR (HGX H100)',
  role: 'compute',
  vendor: 'Supermicro',
  model: 'SYS-821GE-TNHR',

  cpu: {
    sockets: 2,
    coresPerSocket: 60,
    model: 'Intel Xeon Platinum 8562Y+ (5th Gen)',
    tdpPerSocketWatts: 350,
    baseClockGHz: 2.8,
    turboClockGHz: 4.1,
  },

  memory: {
    totalGB: 2048,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 64,
  },

  gpu: {
    platformId: 'hgx-h100-80gb',
    gpusPerNode: 8,
  },

  expansion: {
    pcieSlots: [
      // 8 low-profile slots
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      // 2 full-height slots
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      // 2 optional slots
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 16, // 12 default + 4 optional
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 0,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 24000,
    typicalPowerWatts: 8500,
    idlePowerWatts: 600,
    redundancy: 'N+1',
    psuCount: 8,
    psuCapacityWatts: 3000,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 8,
    widthMm: 482.6,
    depthMm: 1050,
    heightMm: 356,
    weightKg: 95,
    maxAltitudeMeters: 3000,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 8, max: 90 },
    maxBtuPerHour: 29002, // 8500W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 80,
  },

  metadata: {
    productUrl: 'https://www.supermicro.com/en/products/system/gpu/8u/sys-821ge-tnhr',
    notes: '8U HGX H100/H200 platform with extensive I/O and storage capacity',
    validated: true,
  },
} as const;

/**
 * Supermicro SYS-821GE-TNHR with HGX H200
 */
export const SUPERMICRO_SYS821GE_H200: ServerProfile = {
  id: 'supermicro-sys821ge-h200',
  name: 'Supermicro SYS-821GE-TNHR (HGX H200)',
  role: 'compute',
  vendor: 'Supermicro',
  model: 'SYS-821GE-TNHR',

  cpu: {
    sockets: 2,
    coresPerSocket: 60,
    model: 'Intel Xeon Platinum 8562Y+ (5th Gen)',
    tdpPerSocketWatts: 350,
    baseClockGHz: 2.8,
    turboClockGHz: 4.1,
  },

  memory: {
    totalGB: 2048,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 64,
  },

  gpu: {
    platformId: 'hgx-h100-80gb', // H200 uses same platform ID family
    gpusPerNode: 8,
    customName: 'HGX H200 141GB',
  },

  expansion: {
    pcieSlots: [
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'HHHL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 16,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 0,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 24000,
    typicalPowerWatts: 8500,
    idlePowerWatts: 600,
    redundancy: 'N+1',
    psuCount: 8,
    psuCapacityWatts: 3000,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 8,
    widthMm: 482.6,
    depthMm: 1050,
    heightMm: 356,
    weightKg: 95,
    maxAltitudeMeters: 3000,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 8, max: 90 },
    maxBtuPerHour: 29002,
    coolingType: 'air',
    noiseLevelDbA: 80,
  },

  metadata: {
    productUrl: 'https://www.supermicro.com/en/products/system/gpu/8u/sys-821ge-tnhr',
    notes: '8U HGX H200 141GB configuration with 8TB DDR5 capacity',
    validated: true,
  },
} as const;
